<template>
  <div>
    <can action="view" subject="users">
      <div class="ibox">
        <div class="ibox-title">
          <h5>Users</h5>
          <can action="create" subject="add-user">
            <div class="ibox-tools">
              <el-tooltip class="item" content="Add user" placement="left">
                <el-button
                  type="primary"
                  size="small"
                  class="ibox-tool-btn"
                  @click="manageUser(null)"
                  plain
                >
                  <i class="fa fa-plus"></i>
                </el-button>
              </el-tooltip>
            </div>
          </can>
        </div>
        <div
          class="ibox-content ibox-heading"
        >General user login and profile information for employees using the system.</div>
        <div class="ibox-content user-list-content">
          <div class="input-group">
            <input
              type="text"
              class="input form-control"
              placeholder="Search users..."
              v-model="keyword"
            />
            <span class="input-group-btn">
              <button class="btn btn-primary">
                <i class="fa fa-search"></i> Search
              </button>
            </span>
          </div>
          <br />

          <el-table
            :data="results"
            :fit="true"
            :default-sort="{prop: 'display_name'}"
            :empty-text="!results.length && users.length ? `No users found matching ${keyword}` : 'No users'"
            style="width: 100%;"
            max-height="400"
            width="100%"
            row-key="id"
            stripe
          >
            <el-table-column prop="avatar" width="50">
              <template slot-scope="scope">
                <img class="user-avatar" :src="scope.row.avatar || scope.row.gravatar" />
              </template>
            </el-table-column>

            <el-table-column :class-name="'client-link'" label="Name">
              <template slot-scope="scope">
                <el-tooltip content="Edit" placement="right">
                  <a @click="manageUser(scope.row)">{{scope.row.display_name}}</a>
                </el-tooltip>
              </template>
            </el-table-column>

            <el-table-column prop="job_title" label="Job title"></el-table-column>

            <el-table-column label="Email">
              <template slot-scope="scope">
                <i class="fa fa-envelope"></i>
                <span>&nbsp;</span>
                <span>{{scope.row.email}}</span>
              </template>
            </el-table-column>

            <el-table-column width="80" label="Roles">
              <template slot-scope="scope">
                <div class="client-status pull-right">
                  <el-tooltip content="Company Admin" placement="top">
                    <span v-if="scope.row.is_admin" class="label label-info">&nbsp;</span>
                  </el-tooltip>
                  <el-tooltip content="Organization Admin" placement="top">
                    <span v-if="scope.row.is_org_admin" class="label label-primary">&nbsp;</span>
                  </el-tooltip>
                  <el-tooltip content="Inactive" placement="top">
                    <span v-if="!scope.row.user_is_active" class="label label-danger">&nbsp;</span>
                  </el-tooltip>
                  <el-tooltip content="Active" placement="top">
                    <span v-if="scope.row.user_is_active" class="label label-success">&nbsp;</span>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>

            <el-table-column>
              <template slot-scope="scope">
                <el-dropdown
                  trigger="hover"
                  placement="bottom"
                  :show-timeout="100"
                  :hide-on-click="false"
                  v-if="loggedInUser.is_admin"
                >
                  <span class="el-dropdown-link">
                    Actions
                    <i class="el-icon-arrow-down el-icon--right"></i>
                  </span>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item @click.native="manageUser(scope.row)">Edit</el-dropdown-item>
                    <el-dropdown-item
                      v-if="loggedInUser.id!=scope.row.id && scope.row.user_is_active===true"
                      @click.native="deleteUser(scope.row)"
                    >Delete</el-dropdown-item>
                    <el-dropdown-item
                      v-if="loggedInUser.id!=scope.row.id && scope.row.user_is_active===true"
                      @click.native="showPasswordResetPopup(scope.row)"
                    >Change Password</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </can>
    <can action="edit" subject="accounts-users">
      <el-dialog
        v-if="showUserManage"
        :title="selectedUser.id ? 'Edit User' : 'New User'"
        :visible.sync="showUserManage"
        :close-on-click-modal="false"
      >
        <user-manage
          v-if="showUserManage"
          :user="selectedUser"
          :groups="groups"
          :loggedInUser="loggedInUser"
          @cancelled="showUserManage = false"
          @saved="handleSaved"
          @saveError="handleSaveError"
        />
      </el-dialog>

      <el-dialog
        title="Reset Password"
        v-if="restPasswordDialogVisible"
        :visible.sync="restPasswordDialogVisible"
        :append-to-body="true"
      >
        <PasswordOnlyResetFormOnUserList />
      </el-dialog>
    </can>
  </div>
</template>

<script>
import PasswordOnlyResetFormOnUserList from '@/components/auth/PasswordOnlyResetFormOnUserList';
import * as types from '@/store/modules/user/mutation-types';
import Can from '@/components/permissions/Can';
import UserManage from './UserManage';

export default {
  name: 'UserList',
  props: ['users', 'groups'],
  components: { UserManage, PasswordOnlyResetFormOnUserList, Can },
  data: () => ({
    dialogVisible: false,
    showUserManage: false,
    upratingCompany: false,
    selectedUser: null,
    loggedInUser: null,
    restPasswordDialogVisible: false,
    keyword: '',
  }),

  computed: {
    results() {
      const users = this.users.filter(
        user => user.display_name.toLowerCase().indexOf(this.keyword.toLowerCase()) > -1,
      );
      return users.length ? users : [];
    },
  },

  mounted() {
    this.loggedInUser = this.$store.state.user.me;
  },

  beforeUpdate() {
    this.loggedInUser = this.$store.state.user.me;
  },

  methods: {
    manageUser(user) {
      // for some reason ElementUI does not use the latest user object from the list.
      // If a user was updated, and then edited again, the form populated with old data.
      // Therefore we get the user with the same ID from the list. This should be the updated user.
      this.selectedUser = this.users.find(x => user && x.id === user.id) || {
        email: null,
        first_name: null,
        last_name: null,
        job_title: null,
        is_admin: false,
        groups: [],
      };

      this.showUserManage = true;
    },

    showPasswordResetPopup(user) {
      this.selectedUser = user;
      this.$store.commit(`user/${types.UPDATE_USER_PASSWORD_SUCCESS}`, {
        userListSelectedUser: user,
      });
      this.restPasswordDialogVisible = true;
    },

    deleteUser(user) {
      this.selectedUser = user;
      this.$confirm('Are you sure you want to delete this User?')
        .then(() => {
          this.$store.dispatch('user/deleteUser', this.selectedUser);
        })
        .catch(() => null); // prevent uncaught exeptions warning in console...
    },

    handleSaved() {
      this.showUserManage = false;
      this.$notify({
        type: 'success',
        showClose: false,
        duration: 2500,
        title: 'Success',
        message: 'User saved sucessfully',
      });
    },

    handleSaveError() {
      this.$notify({
        type: 'error',
        showClose: false,
        duration: 2500,
        title: 'Error',
        message: 'Please ensure that the email address provided is not already in use.',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.user-list-content {
  padding: 8px;
}

.ibox-tool-btn {
  margin-top: -10px;
}

.user-legend {
  padding: 5px;
}

.client-status {
  margin-right: 15px;

  .label {
    font-size: 8px;
    font-family: monospace;
    padding: 0 2px;
  }
}

.user-avatar {
  height: 25px;
  width: auto;
  border-radius: 13px;
}

.el-dropdown {
  &.v-leave,
  &.v-leave-active {
    .el-dropdown-item .menu-title,
    .el-dropdown__title .menu-title {
      display: none;
    }
  }
}
.el-dropdown-item,
.el-dropdown__title {
  &:hover {
    color: #ffffff !important;
  }
}

.el-dropdown-link {
  cursor: pointer;
  color: #409eff;
}
.el-icon-arrow-down {
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// UserList.vue?0fb0bff8